import Link from "next/link";
import React, { useEffect, useState, useRef } from "react";

import IconWithTooltip from "../IconWithTooltip";
import { formatZipcode } from "@/utils/helpers";
import { openAddressOnMap } from "@/utils/maps";
import { chips } from "@/utils/tsx/filters";
import { FacilityItemProps } from "../FacilityItem";
import ImageCard from "../shared/ImageCard";
import type { StoreListItem } from "@/types/filters";
import type {
  ImageCardCustomStyles,
  ImageCardImageProps
} from "../shared/ImageCard/imageCard.types";

import s from "./storeItem.module.scss";

interface StoreItemProps {
  item: StoreListItem;
}

export const StoreItem = ({ item }: StoreItemProps) => {
  const [visibleItems, setVisibleItems] = useState<number>(9);
  const containerRef = useRef<HTMLUListElement>(null);

  // Atualiza a quantidade de ícones visíveis de acordo com o tamanho do container previnindo quebra de linha e alinhamento incorreto do tooltip
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const itemWidth = 40;
        const itemsFit = Math.floor(containerWidth / itemWidth);
        setVisibleItems(itemsFit);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    const currentContainerRef = containerRef.current;
    if (currentContainerRef) {
      resizeObserver.observe(currentContainerRef);
    }

    handleResize();

    return () => {
      if (currentContainerRef) {
        resizeObserver.unobserve(currentContainerRef);
      }
    };
  }, []);

  if (!item?.address?.street) {
    return <></>;
  }

  // Ajusta o alinhamento do tooltip de acordo com a posição do ícone
  const calculateAlignment = (index: number) => {
    if (index < visibleItems / 3) return "left";
    if (index >= visibleItems - visibleItems / 3) return "right";
    return "center";
  };

  const orderFacilities = (facilities: FacilityItemProps[]) => {
    const orderMap = new Map(chips.map((chip, index) => [chip.title, index]));

    return facilities.sort((a, b) => {
      const indexA = orderMap.get(a.name) ?? Infinity;
      const indexB = orderMap.get(b.name) ?? Infinity;
      return indexA - indexB;
    });
  };

  const orderedFacilities = orderFacilities(item.facilidades);

  const imageProps: ImageCardImageProps = {
    src: `${item.gallery[0]?.url || ""}`,
    alt: "Essa é uma capa para um card de listagem de loja",
    fetchPriority: "low"
  };

  const linkProps = {
    link: `/lojas/${item.slug}`,
    outsideLink: false
  };

  const customStyles: ImageCardCustomStyles = {
    root: {
      borderRadius: "var(--token-border-radius-lg)",
      marginBottom: "var(--token-spacings-xs)",
      maxWidth: "100%"
    },
    image: {
      width: "100%",
      borderBottomLeftRadius: "var(--token-border-radius-lg)",
      borderBottomRightRadius: "var(--token-border-radius-lg)",
      objectFit: "cover",
      height: "21.5rem"
    }
  };

  const actionsCard = (
    <div className={s.store_actions}>
      <Link className="link link-coffee" href={`/lojas/${item.slug}`}>
        Detalhes da loja
      </Link>
      <Link
        className="link link-coffee"
        href={
          item?.maps ||
          openAddressOnMap({ name: item.name, address: item.address }) ||
          ""
        }
        target="_blank"
      >
        Mostrar no mapa
      </Link>
    </div>
  );

  return (
    <ImageCard
      imageProps={imageProps}
      linkProps={linkProps}
      customStyles={customStyles}
      actions={actionsCard}
    >
      <div className={s.list_card_item_content}>
        <div className={s.name} title={item.name}>
          {item.name}
        </div>
        <p>
          {item.address.street}, {item.address?.number || "S/N"}
          <br />
          {item.address.city}-{item.address.state}
          <br />
          {formatZipcode(item.address.zipcode) || ""}
        </p>
      </div>
      <div className={s.list_card_item_content}>
        <ul id="container-list" className={s.facility} ref={containerRef}>
          {orderedFacilities
            .slice(0, visibleItems)
            .map((facility: FacilityItemProps, index: number) => (
              <IconWithTooltip
                key={index}
                icon={facility.icon}
                description={facility.name}
                tooltipAlignment={calculateAlignment(index)}
              />
            ))}
        </ul>
      </div>
    </ImageCard>
  );
};
